/**
 * RetirementHeatmap Component
 * WP-RETIRE-2, WP-RETIRE-5: Heatmap grid with qualitative row labels and scroll support
 *
 * Layout: Table spans full container width.
 * Horizontal scroll enabled on mobile for overflow.
 */

import { useRef, useState } from 'react';
import Tooltip from '@/components/ui/Tooltip';
import type { ProjectionGrid } from '@/utils/retirementCalculations';
import { RETURN_RATE_LABELS } from '@/utils/retirementCalculations';
import type { RetirementEvent } from '@/types/retirement';
import RealReturnInfoPanel from './RealReturnInfoPanel';
import HeatmapCell from './HeatmapCell';
import EventTimeline from '@/components/retirement/EventTimeline';

interface RetirementHeatmapProps {
  projections: ProjectionGrid | null;
  /** Optional events for timeline display (WP-RETIRE-8) */
  events?: RetirementEvent[];
  /** Birth year for converting age columns to calendar years (WP-RETIRE-8) */
  birthYear?: number | null;
}

export default function RetirementHeatmap({ projections, events, birthYear }: RetirementHeatmapProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showScrollHint, setShowScrollHint] = useState(true);

  // Hide scroll hint after user scrolls
  const handleScroll = () => {
    if (scrollRef.current && scrollRef.current.scrollLeft > 10) {
      setShowScrollHint(false);
    }
  };

  if (!projections) {
    return null;
  }

  const { cells, ageColumns, returnRates } = projections;

  // Handle edge case: users age 90+ have no future milestones
  // (MAX_AGE_MILESTONE is 90, so getAgeColumns(90) returns [])
  if (ageColumns.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">No future age milestones to display.</p>
        <p className="text-xs mt-1">
          Projections are calculated up to age 90.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Scroll container - enables horizontal scroll on mobile */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="overflow-x-auto md:overflow-visible"
      >
        {/*
          Table layout strategy:
          - table-fixed: first row defines all column widths
          - border-collapse: borders merge for clean grid lines
          - First column: explicit small width (w-24) for label
          - Age columns: NO width = share remaining space EQUALLY
        */}
        <table
          className="table-fixed w-full border-collapse"
          role="grid"
          aria-label="Retirement funding projections by age and return scenario"
        >
          <thead>
            {/* Label row: "Age" spanning all age columns */}
            <tr>
              <th scope="col" className="w-24 border-0"></th>
              <th
                colSpan={ageColumns.length}
                scope="colgroup"
                className="text-center text-xs font-medium text-gray-500 pb-1 border-0"
              >
                Age
              </th>
            </tr>
            {/* Data header row */}
            <tr>
              {/* Header cell with RRR info panel - fixed small width */}
              <th
                className="w-24 ring-1 ring-inset ring-white"
                scope="col"
              >
                <div className="px-2 py-2 text-left align-bottom">
                  <RealReturnInfoPanel />
                </div>
              </th>
              {/* Age column headers - NO width so they share remaining space equally */}
              {ageColumns.map((age) => (
                <th
                  key={age}
                  className="ring-1 ring-inset ring-white"
                  scope="col"
                >
                  <div className="py-2 text-center text-xs font-medium text-gray-700">
                    {age}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {returnRates.map((rate, rowIndex) => {
              const rateLabel = RETURN_RATE_LABELS[rate];

              return (
                <tr key={rate}>
                  {/* Row header: qualitative label with tooltip (WP-RETIRE-5) */}
                  <th
                    className="w-24 ring-1 ring-inset ring-white"
                    scope="row"
                  >
                    <div className="py-2 text-sm font-medium text-gray-700 text-right pr-2">
                      {rateLabel ? (
                        <Tooltip content={rateLabel.tooltip} position="right">
                          <span className="cursor-help border-b border-dotted border-gray-400">
                            {rateLabel.label}
                          </span>
                        </Tooltip>
                      ) : (
                        `${(rate * 100).toFixed(0)}%`
                      )}
                    </div>
                  </th>
                  {/* Projection cells */}
                  {cells[rowIndex]?.map((cell) => (
                    <HeatmapCell key={`${rate}-${cell.age}`} cell={cell} />
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Event Timeline (WP-RETIRE-8) - shown below grid when events exist */}
      {events && events.length > 0 && birthYear != null && ageColumns.length > 0 && (
        <EventTimeline
          events={events}
          startYear={birthYear + ageColumns[0]!}
          endYear={birthYear + ageColumns[ageColumns.length - 1]!}
        />
      )}

      {/* Mobile scroll hint - fades right edge */}
      {showScrollHint && (
        <div
          className="md:hidden absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent pointer-events-none"
          aria-hidden="true"
        />
      )}

      {/* Mobile scroll indicator dots */}
      {ageColumns.length > 4 && (
        <div className="md:hidden flex justify-center mt-2 gap-1" aria-hidden="true">
          <div className="w-2 h-2 rounded-full bg-gray-400" />
          <div className="w-2 h-2 rounded-full bg-gray-200" />
          <div className="w-2 h-2 rounded-full bg-gray-200" />
        </div>
      )}
    </div>
  );
}
